import React, { useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SidebarNav } from "./SidebarNav";
import { useMobileSidebar } from "./MobileSidebarContext";

export function MobileSidebar({ className }) {
  const { isOpen, close } = useMobileSidebar();

  const handleNavigate = useCallback(() => {
    close();
  }, [close]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <div className={cn("lg:hidden", className)}>
      {/* Full-page overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm",
          "transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={close}
      />

      {/* Full-page sidebar panel */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-[9999] w-full",
          "max-w-[360px] bg-[#20252b] shadow-2xl shadow-black/40",
          "transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Subtle surface treatment */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0)_28%)]" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-sunridge-cyan/30 to-transparent" />

        {/* Close button — top right */}
        <button
          onClick={close}
          className={cn(
            "absolute right-4 top-5 z-10",
            "h-10 w-10 rounded-xl",
            "border border-white/10 bg-white/[0.06]",
            "flex items-center justify-center",
            "text-slate-400 active:text-white",
            "transition-colors duration-150"
          )}
          aria-label="Close navigation"
        >
          <X size={18} />
        </button>

        {/* Navigation content */}
        <div className="relative h-full">
          <SidebarNav isCollapsed={false} onNavigate={handleNavigate} hasCloseControl />
        </div>

        {/* Bottom gradient fade */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#20252b] to-transparent" />
      </div>
    </div>
  );
}

export default MobileSidebar;
